import {Component, Object3D, PhysXComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';

/**
 * Teleports the player to a new location and back.
 * This is used temporarily to teleport the player to a different location when inspecting an object
 * or note through a centralized system.
 */
export class Teleport extends Component {
    static TypeName = 'teleport';

    /**
     * The player object. If omitted the object this component is attached to will be used.
     * @optional
     */
    @property.object()
    playerObject?: Object3D;

    /**
     * If there's a physx component on the player object, it needs to be disabled during teleportation.
     */
    private physxComponent?: PhysXComponent | null;
    private previousLocation: vec3 | null = null;

    start() {
        this.playerObject = this.playerObject ?? this.object;

        this.physxComponent = this.playerObject.getComponent(PhysXComponent);
    }

    /**
     * Teleports a player to a new location
     * @param newLocation the location to teleport to
     */
    teleport(newLocation: vec3) {
        if (this.previousLocation) {
            console.warn('Teleport: teleporting while already teleporting');
            return;
        }
        this.previousLocation = vec3.clone(this.playerObject!.getPositionWorld());

        let wasNotKinematic = false;

        if (this.physxComponent) {
            this.physxComponent.active = false;
            if (!this.physxComponent.kinematic) {
                wasNotKinematic = true;
                this.physxComponent.kinematic = true;
            }
        }

        this.playerObject!.setPositionWorld(newLocation);

        if (this.physxComponent) {
            this.physxComponent.active = true;
            if (!wasNotKinematic) {
                this.physxComponent.kinematic = false;
            }
        }
    }

    /**
     * Teleports a player back to the previous location
     */
    back() {
        if (!this.previousLocation) {
            console.warn('Teleport: teleporting back while not teleporting');
            return;
        }

        let wasNotKinematic = false;

        if (this.physxComponent) {
            this.physxComponent.active = false;
            if (!this.physxComponent.kinematic) {
                wasNotKinematic = true;
                this.physxComponent.kinematic = true;
            }
        }

        this.playerObject!.setPositionWorld(this.previousLocation);

        if (this.physxComponent) {
            this.physxComponent.active = true;
            if (!wasNotKinematic) {
                this.physxComponent.kinematic = false;
            }
        }

        this.previousLocation = null;
    }
}
